import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import envirolinkLogo from '../../assets/envirolink_logo_transparent.png';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { signIn, signInWithOIDC } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<
    'default' | 'verification' | 'pending' | 'rejected'
  >('default');
  const [showPassword, setShowPassword] = useState(false);
  // Lockout feedback: how close this person is, and when they can retry.
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
    null
  );
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Tick the lockout countdown so "try again later" becomes a real number.
  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () =>
      setSecondsLeft(
        Math.max(0, Math.ceil((lockedUntil.getTime() - Date.now()) / 1000))
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const isLocked = lockedUntil !== null && secondsLeft > 0;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('');
      setErrorType('default');
      setAttemptsRemaining(null);
      setLockedUntil(null);

      await signIn(data.email, data.password, { skipNavigation: true });

      // Auth succeeded — navigate immediately
      navigate('/select-mode');
    } catch (err: any) {
      // api.ts's response interceptor replaces the axios error with a plain
      // Error whose `message` IS the FastAPI detail, keeping the original on
      // `.originalError`. Reading only `err.response.data.detail` therefore
      // always yielded '' — which is why server login messages (including
      // "temporarily locked") never reached the user. Handle both shapes.
      const axiosErr = err.originalError ?? err;
      const detail =
        axiosErr.response?.data?.detail ||
        (typeof err.message === 'string' ? err.message : '') ||
        '';
      const headers = axiosErr.response?.headers ?? {};
      const remainingHeader = headers['x-login-attempts-remaining'];
      const lockedHeader = headers['x-account-locked-until'];
      setAttemptsRemaining(
        remainingHeader === undefined ? null : Number(remainingHeader)
      );
      setLockedUntil(lockedHeader ? new Date(lockedHeader) : null);

      if (
        detail.includes('not verified') ||
        detail.includes('Email not verified')
      ) {
        setErrorType('verification');
        setError(
          'Please verify your email before logging in. Check your inbox for the verification link.'
        );
      } else if (
        detail.includes('pending') ||
        detail.includes('Pending approval')
      ) {
        setErrorType('pending');
        setError('Your account is pending administrator approval.');
        setTimeout(() => navigate('/pending-approval'), 2000);
      } else if (
        detail.includes('rejected') ||
        detail.includes('Account rejected')
      ) {
        setErrorType('rejected');
        setError(
          'Your account registration was not approved. Please contact support for more information.'
        );
      } else if (
        detail.includes('locked') ||
        detail.includes('temporarily locked')
      ) {
        setErrorType('default');
        // Server wording carries the real remaining duration; the countdown
        // below keeps it honest as the clock runs.
        setError(detail);
      } else {
        setErrorType('default');
        // From the third failure the API appends how many tries are left.
        setError(detail || 'Invalid email or password');
      }
    }
  };

  const handleOIDCLogin = () => {
    signInWithOIDC();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-0">
            <img
              src={envirolinkLogo}
              alt="EnviroLink"
              className="h-48 sm:h-96 w-auto object-contain"
            />
          </div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Sign in to EnviroLink
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Storm-Water Compliance Made Simple
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div
              className={`rounded-md p-4 ${
                errorType === 'verification'
                  ? 'bg-yellow-50'
                  : errorType === 'pending'
                    ? 'bg-blue-50'
                    : errorType === 'rejected'
                      ? 'bg-red-50'
                      : 'bg-red-50'
              }`}
            >
              <p
                className={`text-sm ${
                  errorType === 'verification'
                    ? 'text-yellow-800'
                    : errorType === 'pending'
                      ? 'text-blue-800'
                      : 'text-red-800'
                }`}
              >
                {error}
              </p>
              {isLocked && (
                <p className="mt-1 text-sm font-medium tabular-nums text-red-800">
                  You can try again in {Math.floor(secondsLeft / 60)}:
                  {String(secondsLeft % 60).padStart(2, '0')}
                </p>
              )}
              {/* Once someone has failed enough to be warned, stop making them
                  hunt for the small link below — this is what they need. */}
              {(isLocked ||
                (attemptsRemaining !== null && attemptsRemaining < 3)) && (
                <Link
                  to="/forgot-password"
                  className="mt-2 inline-block text-sm font-semibold text-red-800 underline hover:text-red-900"
                >
                  Reset your password
                </Link>
              )}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                {...register('email')}
                id="email"
                type="email"
                autoComplete="email"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                {...register('password')}
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-0 z-20 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              Forgot your password?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleOIDCLogin}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Sign in with SSO
              </button>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need access? Contact your administrator for an invitation.
            </p>
          </div>

          {/* Dev-only login - only visible in development */}
          {import.meta.env.DEV && <DevLoginButton />}
        </form>
      </div>
    </div>
  );
}

// Dev-only component - bypasses auth for local testing
function DevLoginButton() {
  const { login } = useAuthStore();

  const handleDevLogin = () => {
    const devUser = {
      id: 'dev-user-001',
      email: 'dev@envirolink.local',
      firstName: 'Dev',
      lastName: 'User',
      role: 'admin' as const,
      organizationId: 'org-dev',
      permissions: ['read', 'write', 'admin'],
      authProvider: 'local' as const,
      mfaEnabled: false,
      isActive: true,
      preferences: {
        notifications: { email: true, sms: false, discord: false },
        timezone: 'America/Chicago',
        dateFormat: 'MMM d, yyyy',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    login(devUser as any, 'dev-token', 'dev-refresh-token');
    // Redirect to mode selection page
    window.location.href = '/select-mode';
  };

  return (
    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
      <p className="text-xs text-yellow-800 mb-2 text-center">
        ⚠️ Development Mode Only
      </p>
      <button
        type="button"
        onClick={handleDevLogin}
        className="w-full flex justify-center py-2 px-4 border border-yellow-400 rounded-md shadow-sm text-sm font-medium text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
      >
        Dev Login (Skip Auth)
      </button>
    </div>
  );
}
